import { PHASE, PLAYER } from "../../../common/constants.ts";
import type { Player } from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, logEvent } from "../../util/index.ts";
import { player, team } from "../index.ts";
import accept from "./accept.ts";
import { minContractExp } from "../player/setContract.ts";
import get from "./get.ts";
import { rosterImprovement } from "../team/rosterComposition.baseball.ts";
import { teamBaseballBudget } from "../team/aiFinances.baseball.ts";
import { roomForMaxContract } from "../team/maxContracts.baseball.ts";

type Bid = { tid: number; amount: number; exp: number; score: number };

// The user's offer waits a few days for an answer, so other clubs have time to beat it
export type UserOffer = {
	tid: number;
	amount: number;
	exp: number;
	daysLeft: number;
};
type MarketPlayer = Player & {
	freeAgencyMarket?: { season: number; bids: Bid[]; userOffer?: UserOffer };
};

// Two or three days to think it over, fixed per player so it doesn't change on reload
const decisionDays = (p: Player) => 2 + (p.pid % 2);

export const getUserOffer = (p: Player) => {
	const market = (p as MarketPlayer).freeAgencyMarket;
	return market?.season === g.get("season") ? market.userOffer : undefined;
};
export const contractBaseYear = () =>
	g.get("season") - (g.get("phase") <= PHASE.AFTER_TRADE_DEADLINE ? 1 : 0);

const LONG_TERM_DISCOUNT = 0.03;
const LONG_TERM_FLOOR = 0.7;

// Dollar utility is relative to each club's mood-adjusted asking price. Mood already
// includes the player's money, winning, fame and loyalty traits.
export const termFactor = (
	age: number,
	potential: number,
	overall: number,
	years: number,
) => {
	const preferred = age >= 30 ? 3 : potential - overall >= 10 ? 2 : 4;
	// He trades salary for security: each year past the length he prefers takes some off the yearly
	// salary (down to a floor), each year short of it adds a little
	return (
		Math.max(LONG_TERM_FLOOR, 1 - Math.max(0, years - preferred) * LONG_TERM_DISCOUNT) +
		Math.max(0, preferred - years) * 0.025
	);
};
export const validOffer = (
	amount: number,
	years: number,
	min: number,
	max: number,
	minYears: number,
	maxYears: number,
) =>
	Number.isFinite(amount) &&
	Number.isInteger(amount) &&
	amount >= min &&
	amount <= max &&
	Number.isInteger(years) &&
	years >= minYears &&
	years <= maxYears;

const asking = async (p: Player, tid: number, years: number) => {
	const mood = await player.moodInfo(p, tid);
	const r = p.ratings.at(-1)!;
	return {
		willing: mood.willing,
		amount: Math.max(
			g.get("minContract"),
			mood.contractAmount *
				termFactor(g.get("season") - p.born.year, r.pot, r.ovr, years),
		),
	};
};
const canBid = async (p: Player, tid: number, amount: number) => {
	const t = await idb.cache.teams.get(tid);
	if (!t || t.disabled || g.get("userTids").includes(tid)) return false;
	if (g.get("forceHistoricalRosters")) return false;
	// Reject unaffordable bids before the expensive roster assignment.
	const payroll = await team.getPayroll(tid);
	if (
		!(
			payroll + amount <= (await teamBaseballBudget(tid)) ||
			(amount <= g.get("minContract") && g.get("salaryCapType") !== "none")
		)
	)
		return false;
	const roster = await idb.cache.players.indexGetAll("playersByTid", tid);
	if (!roomForMaxContract(roster, amount, p.pid)) return false;
	const full = roster.length >= g.get("maxRosterSize");
	return (
		rosterImprovement(
			roster,
			p,
			g.get("maxRosterSize"),
			g.get("dh") !== "none",
		) >= (full ? 3 : 0.1)
	);
};
export const getMarket = async (p: MarketPlayer) => {
	if (!p.freeAgencyMarket || p.freeAgencyMarket.season !== g.get("season")) {
		const bids: Bid[] = [];
		const years = Math.max(
			g.get("minContractLength"),
			Math.min(
				g.get("maxContractLength"),
				g.get("season") - p.born.year >= 30 ? 3 : 2,
			),
		);
		for (const t of await idb.cache.teams.getAll()) {
			if (g.get("userTids").includes(t.tid) || t.disabled) continue;
			const ask = await asking(p, t.tid, years);
			const amount = Math.min(
				g.get("maxContract"),
				Math.ceil(ask.amount * (1 + ((p.pid + t.tid * 7) % 9) / 100)),
			);
			if (
				ask.willing &&
				amount >= ask.amount &&
				(await canBid(p, t.tid, amount))
			)
				bids.push({
					tid: t.tid,
					amount,
					exp: Math.max(contractBaseYear() + years, minContractExp(p)),
					score: amount / ask.amount,
				});
		}
		p.freeAgencyMarket = {
			season: g.get("season"),
			bids: bids.sort((a, b) => b.score - a.score || a.tid - b.tid).slice(0, 3),
		};
		await idb.cache.players.put(p);
	}
	const valid: Bid[] = [];
	for (const bid of p.freeAgencyMarket.bids) {
		if (
			!validOffer(
				bid.amount,
				bid.exp - contractBaseYear(),
				g.get("minContract"),
				g.get("maxContract"),
				g.get("minContractLength"),
				maxYearsFor(p),
			)
		)
			continue;
		const ask = await asking(p, bid.tid, bid.exp - contractBaseYear());
		if (ask.willing && (await canBid(p, bid.tid, bid.amount)))
			valid.push({ ...bid, score: bid.amount / ask.amount });
	}
	return valid.sort((a, b) => b.score - a.score);
};
// A player under 21 signs through the season he turns 21, even past the league's usual max length
const maxYearsFor = (p: Player) =>
	Math.max(g.get("maxContractLength"), minContractExp(p) - contractBaseYear());

// Share the exact instant-sign terms between the free-agent list and offer acceptance.
// contractAmount is the team's mood-adjusted base salary, before the term adjustment.
export const getSigningContract = (
	p: Player,
	contractAmount: number,
	expInput: number,
) => {
	const exp = Math.max(expInput, minContractExp(p));
	const r = p.ratings.at(-1)!;
	const askAmount = Math.max(
		g.get("minContract"),
		contractAmount * termFactor(g.get("season") - p.born.year, r.pot, r.ovr, exp - contractBaseYear()),
	);
	return {
		amount: Math.min(
			g.get("maxContract"),
			Math.max(g.get("minContract"), Math.ceil(askAmount * 0.95)),
		),
		exp,
	};
};

export const evaluateOffer = async (
	p: Player,
	amount: number,
	expInput: number,
) => {
	const exp = Math.max(expInput, minContractExp(p));
	const years = exp - contractBaseYear();
	if (
		!validOffer(
			amount,
			years,
			g.get("minContract"),
			g.get("maxContract"),
			g.get("minContractLength"),
			maxYearsFor(p),
		)
	)
		return {
			error: "Enter a salary and contract length within the league limits.",
		};
	if (g.get("challengeNoFreeAgents") && amount > g.get("minContract"))
		return {
			error: "This league only allows free agents on minimum contracts.",
		};
	const mood = await player.moodInfo(p, g.get("userTid"));
	if (!mood.willing)
		return { error: "This player refuses to negotiate with your team." };
	// Only his own asking price counts: other clubs' offers don't raise it
	const required = getSigningContract(p, mood.contractAmount, exp).amount;
	return {
		required,
		accepted: amount >= required,
		rejected: amount < required * 0.7,
		exp,
	};
};
// Re-signing one of the user's players: other clubs bid too, and to keep him the user has to
// match the best of them relative to what he asks each team. His mood toward the user's team
// (traits included) already lowers what he asks to stay, so a happy, loyal player is cheaper.
export const resignRequirement = async (p: Player, exp: number) => {
	const best = (await getMarket(p as MarketPlayer))[0];
	if (!best) {
		return;
	}
	const ask = await asking(p, g.get("userTid"), exp - contractBaseYear());
	if (!ask.willing) {
		return;
	}
	return {
		best,
		teamName: teamName(best.tid),
		required: Math.min(
			g.get("maxContract"),
			Math.ceil(ask.amount * best.score),
		),
	};
};

// Register the user's offer. Nothing is signed yet: the player answers when daysLeft runs out.
export const makeUserOffer = async (p: Player, amount: number, exp: number) => {
	await getMarket(p as MarketPlayer);
	const market = (p as MarketPlayer).freeAgencyMarket!;
	market.userOffer = {
		tid: g.get("userTid"),
		amount,
		exp,
		daysLeft: decisionDays(p),
	};
	await idb.cache.players.put(p);
	return market.userOffer;
};

export const withdrawUserOffer = async (p: Player) => {
	const market = (p as MarketPlayer).freeAgencyMarket;
	if (market?.userOffer) {
		delete market.userOffer;
		await idb.cache.players.put(p);
	}
};

const userOfferScore = async (p: Player, offer: UserOffer) => {
	const ask = await asking(p, offer.tid, offer.exp - contractBaseYear());
	return ask.willing ? offer.amount / ask.amount : 0;
};

// How far a club will go for him: a little over his asking price, more if he fills a hole
const bidCap = async (p: Player, tid: number, askAmount: number) => {
	const roster = await idb.cache.players.indexGetAll("playersByTid", tid);
	const gain = rosterImprovement(
		roster,
		p,
		g.get("maxRosterSize"),
		g.get("dh") !== "none",
	);
	const need = Math.min(1, gain / 15);
	return Math.min(
		g.get("maxContract"),
		Math.ceil(askAmount * (1.05 + 0.15 * need)),
	);
};

const notifyUser = async (p: Player, text: string) => {
	await logEvent({
		type: "freeAgent",
		text,
		pids: [p.pid],
		tids: [g.get("userTid")],
		showNotification: true,
		saveToDb: false,
	});
};

const playerLink = (p: Player) =>
	`<a href="${helpers.leagueUrl(["player", p.pid])}">${p.firstName} ${p.lastName}</a>`;
const teamName = (tid: number) =>
	`${g.get("teamInfoCache")[tid]?.region ?? ""} ${g.get("teamInfoCache")[tid]?.name ?? ""}`.trim();

// Clubs that still want him answer a pending user offer by raising their own, within reason
const answerUserOffer = async (p: MarketPlayer, offer: UserOffer) => {
	const userScore = await userOfferScore(p, offer);
	for (const bid of p.freeAgencyMarket!.bids) {
		const years = bid.exp - contractBaseYear();
		const ask = await asking(p, bid.tid, years);
		if (!ask.willing || bid.amount / ask.amount > userScore) {
			continue;
		}
		const target = Math.ceil(ask.amount * (userScore + 0.01));
		if (
			target <= (await bidCap(p, bid.tid, ask.amount)) &&
			(await canBid(p, bid.tid, target))
		) {
			bid.amount = target;
			bid.score = target / ask.amount;
			await notifyUser(
				p,
				`The ${teamName(bid.tid)} topped your offer for ${playerLink(p)}: ${helpers.formatCurrency(target / 1000, "M")}/yr thru ${bid.exp}.`,
			);
			// One counter per day is enough to keep the auction moving
			break;
		}
	}
};

const decideUserOffer = async (p: MarketPlayer, offer: UserOffer) => {
	const bids = await getMarket(p);
	const best = bids[0];
	const userScore = await userOfferScore(p, offer);
	delete p.freeAgencyMarket;
	await idb.cache.players.put(p);

	if (userScore >= 0.95 && (!best || userScore >= best.score)) {
		// A full roster doesn't block the signing: the usual roster check then asks the
		// user to open a spot before the next game
		let error: string | undefined;
		if (offer.tid !== g.get("userTid")) {
			error = "you switched teams";
		} else {
			const negotiation = await get(p.pid);
			const result = await accept({
				negotiation:
					typeof negotiation === "string"
						? { pid: p.pid, tid: offer.tid, resigning: false }
						: negotiation,
				amount: offer.amount,
				exp: offer.exp,
				dryRun: false,
			});
			if (typeof result === "string") {
				error = result;
			}
		}
		if (error === undefined) {
			await notifyUser(
				p,
				`${playerLink(p)} accepted your offer: ${helpers.formatCurrency(offer.amount / 1000, "M")}/yr thru ${offer.exp}.`,
			);
			return;
		}
		await notifyUser(
			p,
			`${playerLink(p)} would accept your offer, but the signing could not go through: ${error}.`,
		);
	}

	if (best && best.score >= 0.95) {
		await player.sign(
			p,
			best.tid,
			{ amount: best.amount, exp: best.exp },
			g.get("phase"),
		);
		await idb.cache.players.put(p);
		await team.rosterAutoSort(best.tid);
		await notifyUser(
			p,
			`${playerLink(p)} chose the ${teamName(best.tid)}: ${helpers.formatCurrency(best.amount / 1000, "M")}/yr thru ${best.exp}.`,
		);
	} else if (userScore < 0.95) {
		await notifyUser(
			p,
			`${playerLink(p)} turned down your offer and is still on the market. You can make another one.`,
		);
	}
};

export const resolveMarkets = async () => {
	for (const raw of await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.FREE_AGENT,
	)) {
		const p = raw as MarketPlayer;
		if (!p.freeAgencyMarket || p.freeAgencyMarket.season !== g.get("season"))
			continue;

		const offer = p.freeAgencyMarket.userOffer;
		if (offer) {
			await answerUserOffer(p, offer);
			offer.daysLeft -= 1;
			await idb.cache.players.put(p);
			if (offer.daysLeft <= 0) {
				await decideUserOffer(p, offer);
			}
			continue;
		}

		const bids = await getMarket(p);
		const winner = bids.find((b) => b.score >= 0.95);
		if (!winner) continue;
		delete p.freeAgencyMarket;
		await player.sign(
			p,
			winner.tid,
			{ amount: winner.amount, exp: winner.exp },
			g.get("phase"),
		);
		await idb.cache.players.put(p);
		await team.rosterAutoSort(winner.tid);
	}
};
